import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";

type Context = {
  currentPage: number;
  totalPages: number;
  nextPage: (() => void) | false;
  prevPage: (() => void) | false;
  setPage: Dispatch<SetStateAction<number>>;
  setTotalPages: Dispatch<SetStateAction<number>>;
};

const PaginationContext = createContext<Context>({
  currentPage: 1,
  totalPages: 1,
  nextPage: false,
  prevPage: false,
  setPage: () => {},
  setTotalPages: () => {},
});

type Provider = PropsWithChildren<{
  defaultPage?: number;
  defaultTotalPages?: number;
  /** When set, page is driven by the query (e.g. useContents) instead of internal state. */
  page?: number;
  totalPages?: number;
  nextPage?: (() => void) | false;
  prevPage?: (() => void) | false;
}>;

export const PaginationProvider = ({
  defaultPage,
  defaultTotalPages,
  page: pageProp,
  totalPages: totalPagesProp,
  nextPage: nextPageProp,
  prevPage: prevPageProp,
  children,
}: Provider) => {
  const [pageState, setPage] = useState(defaultPage || 1);
  const [totalPagesState, setTotalPages] = useState(defaultTotalPages || 1);

  const page = pageProp ?? pageState;
  const totalPages = totalPagesProp ?? totalPagesState;

  const nextPage = useMemo(
    () =>
      nextPageProp !== undefined
        ? nextPageProp
        : page !== totalPages
          ? () => {
              setPage((p) => p + 1);
            }
          : false,
    [nextPageProp, page, totalPages],
  );
  const prevPage = useMemo(
    () =>
      prevPageProp !== undefined
        ? prevPageProp
        : page !== 1
          ? () => {
              setPage((p) => p - 1);
            }
          : false,
    [prevPageProp, page],
  );
  return (
    <PaginationContext.Provider
      value={{
        currentPage: page,
        totalPages,
        setTotalPages,
        setPage,
        nextPage,
        prevPage,
      }}
    >
      {children}
    </PaginationContext.Provider>
  );
};

export const usePagination = () => useContext(PaginationContext);
